/**
 * Mock Provider — a fully offline, deterministic stand-in for an LLM.
 *
 * Maps prompt keywords onto the creative vocabulary the way a model with
 * function calling would, and returns the same message shape real adapters
 * will return. Deterministic: the same prompt always yields the same brief
 * (seeded by a string hash), so the Playground is reproducible and the
 * pipeline is testable without any network.
 */

import { AIProvider } from './providerInterface';
import { CREATIVE_BRIEF_FUNCTION_NAME } from './functionSchema';

/** djb2 — stable string hash for deterministic seeds. */
export const hashString = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) >>> 0;
  return h;
};

const AUDIENCE_KEYWORDS = [
  [['burger', 'pizza', 'restaurant', 'menu', 'dining', 'bistro'], 'restaurant'],
  [['coffee', 'cafe', 'bakery', 'brunch'], 'cafe'],
  [['gym', 'fitness', 'workout', 'training', 'crossfit'], 'gym'],
  [['clinic', 'doctor', 'medical', 'dental', 'health check'], 'medical'],
  [['salon', 'hair', 'spa', 'beauty', 'nails', 'makeup'], 'salon'],
  [['property', 'real estate', 'apartment', 'house for', 'open house', 'realtor'], 'real-estate'],
  [['school', 'course', 'bootcamp', 'university', 'learn', 'class'], 'education'],
  [['fashion', 'clothing', 'boutique', 'collection', 'wear'], 'fashion'],
  [['festival', 'carnival', 'parade'], 'festival'],
  [['travel', 'trip', 'vacation', 'tour', 'getaway', 'resort'], 'travel'],
  [['kids', 'children', 'birthday', 'toy'], 'kids'],
  [['luxury', 'exclusive', 'premium brand', 'vip'], 'luxury'],
  [['conference', 'summit', 'hiring', 'business', 'agency', 'startup'], 'corporate'],
];

const MOOD_KEYWORDS = [
  [['calm', 'elegant', 'graceful'], 'elegant'],
  [['energetic', 'exciting', 'high energy', 'loud'], 'energetic'],
  [['fresh', 'clean look', 'crisp'], 'fresh'],
  [['healthy', 'wellness', 'organic'], 'healthy'],
  [['warm', 'cozy', 'inviting'], 'warm'],
  [['luxurious', 'luxury feel', 'opulent'], 'luxury'],
  [['playful', 'fun', 'quirky'], 'playful'],
  [['bold', 'strong', 'powerful'], 'bold'],
  [['minimal', 'minimalist', 'simple'], 'minimal'],
  [['premium', 'upscale', 'high end'], 'premium'],
];

const STYLE_KEYWORDS = [
  [['modern'], 'modern'],
  [['corporate look', 'professional'], 'corporate'],
  [['creative', 'artsy'], 'creative'],
  [['minimal', 'minimalist'], 'minimal'],
  [['bold', 'in your face'], 'bold'],
  [['elegant', 'refined'], 'elegant'],
  [['street', 'urban', 'grunge'], 'street'],
  [['editorial', 'magazine'], 'editorial'],
  [['luxury', 'high end', 'premium'], 'luxury'],
];

const matchKeyword = (prompt, table) => {
  const lower = prompt.toLowerCase();
  for (const [keywords, value] of table) {
    if (keywords.some((k) => lower.includes(k))) return value;
  }
  return null;
};

export class MockProvider extends AIProvider {
  constructor() {
    super({
      id: 'mock',
      name: 'Mock (offline)',
      capabilities: { functionCalling: true, streaming: false, offline: true },
    });
  }

  isReady() {
    return true;
  }

  async generate(prompt /* , context */) {
    const userText = prompt?.user || '';
    const args = { audience: matchKeyword(userText, AUDIENCE_KEYWORDS) || 'corporate' };

    const mood = matchKeyword(userText, MOOD_KEYWORDS);
    if (mood) args.mood = mood;
    const style = matchKeyword(userText, STYLE_KEYWORDS);
    if (style) args.brandStyle = style;

    return {
      type: 'function_call',
      name: CREATIVE_BRIEF_FUNCTION_NAME,
      arguments: args,
      usage: { provider: 'mock', promptHash: hashString(userText) },
    };
  }
}

export const mockProvider = new MockProvider();
